/*
 * benvel-lookbook-index.js — Lookbook index section module.
 * Story 16.3 (Epic 16 — Lookbook index: campaign cards + look cards).
 *
 * Scope:
 *   1. Consent-gated analytics events for campaign/look card clicks.
 *   2. Look-save heart toggle (§D5 additive extension to Story 14.1).
 *      Handles [data-bv-wishlist-heart] buttons rendered by the look-save
 *      branch of wishlist-heart-button.liquid. Product-save hearts
 *      ([data-wishlist-heart]) continue to be handled by benvel-wishlist.js.
 *
 * localStorage keys (look-save, per §D5 schema):
 *   benvel.wishlist.looks.<slug>  → "1" (saved)
 *   PII discipline: only opaque look slugs stored.
 *
 * Analytics events (consent-gated per Story 8.1 kernel):
 *   benvel:lookbook-card-click   { kind, slug, position, source_page }
 *   benvel:wishlist-save         { entity_type: 'look', entity_id, source_page }
 *   benvel:wishlist-remove       { entity_type: 'look', entity_id, source_page }
 *
 * Bundle budget target: ≤ 3KB minified+gzipped.
 */

(function () {
  "use strict";

  // ─── Look-save helpers ──────────────────────────────────────────────────

  var LOOKS_NS = "benvel.wishlist.looks.";

  function lookStorageKey(slug) {
    return LOOKS_NS + slug;
  }

  function isLookSaved(slug) {
    try {
      return localStorage.getItem(lookStorageKey(slug)) === "1";
    } catch (_) {
      return false;
    }
  }

  function setLookSaved(slug, saved) {
    try {
      if (saved) {
        localStorage.setItem(lookStorageKey(slug), "1");
      } else {
        localStorage.removeItem(lookStorageKey(slug));
      }
    } catch (_) {
      // Quota / private mode — silently skip
    }
  }

  // ─── Analytics ──────────────────────────────────────────────────────────

  function consentGranted() {
    try {
      return Boolean(
        window.benvel &&
          window.benvel.consent &&
          typeof window.benvel.consent.has === "function" &&
          window.benvel.consent.has("analytics"),
      );
    } catch (_) {
      return false;
    }
  }

  function emitAnalytics(name, detail) {
    if (!consentGranted()) return;
    document.dispatchEvent(
      new CustomEvent(name, { bubbles: true, detail }),
    );
  }

  // ─── Look-heart state sync ───────────────────────────────────────────────

  function applyLookHeartState(btn, saved) {
    btn.setAttribute("aria-pressed", saved ? "true" : "false");
    if (saved) {
      btn.classList.add("bv-wishlist-heart--saved");
    } else {
      btn.classList.remove("bv-wishlist-heart--saved");
    }
    var label = saved
      ? btn.dataset.bvLabelRemove
      : btn.dataset.bvLabelAdd;
    if (label) {
      btn.setAttribute("aria-label", label);
    }
  }

  function syncLookHearts() {
    document.querySelectorAll("[data-bv-wishlist-heart]").forEach(function (btn) {
      var slug = btn.dataset.bvEntityId;
      if (!slug) return;
      applyLookHeartState(btn, isLookSaved(slug));
    });
  }

  // ─── Look-heart click ────────────────────────────────────────────────────

  function handleLookHeartClick(event) {
    var btn = event.target.closest("[data-bv-wishlist-heart]");
    if (!btn) return;

    var slug = btn.dataset.bvEntityId;
    var sourcePage = btn.dataset.bvSourcePage || "lookbook-index";
    if (!slug) return;

    event.stopPropagation(); // Don't trigger card-click analytics

    var nowSaved = !isLookSaved(slug);
    setLookSaved(slug, nowSaved);
    applyLookHeartState(btn, nowSaved);

    emitAnalytics(
      nowSaved ? "benvel:wishlist-save" : "benvel:wishlist-remove",
      { entity_type: "look", entity_id: slug, source_page: sourcePage },
    );
  }

  // ─── Card-click analytics ────────────────────────────────────────────────

  function handleCardClick(event) {
    // Skip clicks that originated on a look-heart button
    if (event.target.closest("[data-bv-wishlist-heart]")) return;

    const card = event.target.closest(
      "[data-bv-card-kind='campaign'], [data-bv-card-kind='look']",
    );
    if (!card) return;

    const kind = card.dataset.bvCardKind;
    const slug =
      card.dataset.bvCampaignSlug || card.dataset.bvLookSlug || "";
    const position = Number(card.dataset.bvCardPosition) || 0;

    emitAnalytics("benvel:lookbook-card-click", {
      kind,
      slug,
      position,
      source_page: "lookbook-index",
    });
  }

  // ─── Init ────────────────────────────────────────────────────────────────

  function init() {
    syncLookHearts();
    document.addEventListener("click", handleLookHeartClick);

    const sections = document.querySelectorAll(
      "[data-bv-section='lookbook-index']",
    );
    sections.forEach(function (section) {
      section.addEventListener("click", handleCardClick);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
